import { useEffect, useState, useCallback } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader, StatusChip } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { ArrowsClockwise, Receipt, Eye } from "@phosphor-icons/react";

export default function MyPayslips() {
    const [year, setYear] = useState(new Date().getFullYear());
    const [profile, setProfile] = useState(null);
    const [slips, setSlips] = useState([]);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    // Founder-only "print on request" — pick any employee to view their slips.
    const [employees, setEmployees] = useState([]);
    const [empId, setEmpId] = useState("");

    // Load employees list for the picker.
    useEffect(() => {
        api.get("/employees").then(({ data }) => setEmployees(data.filter((e) => e.mode !== "contract")))
           .catch(() => {});
    }, []);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = { year };
            if (empId) params.employee_id = empId;
            const [p, s] = await Promise.all([
                api.get("/me/profile").catch(() => ({ data: null })),
                api.get("/me/payslips", { params }),
            ]);
            // When looking at another employee, the profile card should show them,
            // not the founder. Fetch minimal employee info instead.
            if (empId) {
                const e = employees.find((x) => x.id === empId);
                if (e) setProfile({ name: e.name, tier: e.tier, base_salary: e.base_salary, department: e.department });
            } else {
                setProfile(p.data);
            }
            setSlips(s.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [year, empId, employees]);
    useEffect(() => { refresh(); }, [refresh]);

    const openDetail = async (id) => {
        try { const { data } = await api.get(`/me/payslips/${id}`); setDetail(data); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Payslips (founder only)"
                sub="Pick an employee to view or print any month's payslip. Staff no longer see this — the founder generates on request."
                right={
                    <div className="flex items-center gap-2">
                        <select value={empId} onChange={(e) => setEmpId(e.target.value)}
                                className="border rounded-sm px-2 py-1.5 text-sm h-9 bg-white"
                                style={{ borderColor: "var(--se-border)" }}
                                data-testid="my-emp-picker">
                            <option value="">Me (self)</option>
                            {employees.map((e) => (
                                <option key={e.id} value={e.id}>{e.name}{e.department ? ` · ${e.department}` : ""}</option>
                            ))}
                        </select>
                        <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24" data-testid="my-year" />
                        <Button variant="outline" onClick={refresh} disabled={loading} data-testid="my-refresh">
                            <ArrowsClockwise size={14} className="mr-2" /> Refresh
                        </Button>
                    </div>
                } />

            <div className="p-8 space-y-6">
                {profile ? (
                    <div className="bg-white border rounded-md p-5 grid grid-cols-4 gap-3" style={{ borderColor: "var(--se-border)" }}>
                        <div>
                            <div className="text-xs text-stone-500">Employee</div>
                            <div className="font-medium">{profile.name}</div>
                            <div className="text-[10px] text-stone-500">{profile.employee_code}</div>
                        </div>
                        <div>
                            <div className="text-xs text-stone-500">Tier</div>
                            <div className="capitalize">{profile.tier}</div>
                        </div>
                        <div>
                            <div className="text-xs text-stone-500">Department / Location</div>
                            <div className="text-sm">{profile.department || "—"} · {profile.location || "—"}</div>
                        </div>
                        <div>
                            <div className="text-xs text-stone-500">Base salary</div>
                            <div className="se-num font-medium">₹ {inr(profile.base_salary || 0)}</div>
                        </div>
                    </div>
                ) : (
                    <div className="text-xs text-red-700 border rounded-md p-4" style={{ borderColor: "#fecaca", background: "#fef2f2" }}>
                        Your login is not linked to an employee record yet. Ask HR to link it.
                    </div>
                )}

                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <div className="px-5 py-3 border-b font-serif text-lg" style={{ borderColor: "var(--se-border)" }}>
                        {year} — {slips.length} payslip{slips.length === 1 ? "" : "s"}
                    </div>
                    <table className="se-table" data-testid="my-slips-table">
                        <thead>
                            <tr>
                                <th>Month</th><th className="text-right">Days</th><th className="text-right">Gross</th>
                                <th className="text-right">Deductions</th><th className="text-right">Net</th>
                                <th>Status</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {slips.length === 0 ? (
                                <tr><td colSpan="7" className="py-10 text-center text-stone-500">
                                    No payslips for {year} yet.
                                </td></tr>
                            ) : slips.map((r) => (
                                <tr key={r.id} data-testid={`my-slip-${r.month}`}>
                                    <td className="font-medium">{r.month}</td>
                                    <td className="se-num text-right text-xs">
                                        {r.effective_working_days || r.worked_working_days} / {r.working_days_in_month}
                                        {r.lop_days > 0 ? <div className="text-[10px] text-amber-700">LOP {r.lop_days}</div> : null}
                                    </td>
                                    <td className="se-num text-right font-medium">₹ {inr(r.gross_earned)}</td>
                                    <td className="se-num text-right text-xs">
                                        ₹ {inr((r.tds_deduction || 0) + (r.pf_employee_deduction || 0) + (r.pt_deduction || 0) + (r.advance_recovery || 0))}
                                    </td>
                                    <td className="se-num text-right font-medium" style={{ color: "#0f766e" }}>₹ {inr(r.net_payable)}</td>
                                    <td><StatusChip status={r.status} /></td>
                                    <td className="text-right">
                                        <Button size="sm" variant="outline" onClick={() => openDetail(r.id)} data-testid={`my-slip-view-${r.month}`}>
                                            <Eye size={13} className="mr-1" /> View
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <PayslipDialog slip={detail} onClose={() => setDetail(null)} profile={profile} />
        </>
    );
}

function PayslipDialog({ slip, onClose, profile }) {
    if (!slip) return null;
    const deductions = (slip.tds_deduction || 0) + (slip.pf_employee_deduction || 0) + (slip.pt_deduction || 0) + (slip.advance_recovery || 0);
    return (
        <Dialog open={!!slip} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-2xl" data-testid="my-slip-dialog">
                <DialogHeader><DialogTitle>
                    <Receipt size={16} className="inline mr-2" />Payslip — {slip.month}
                </DialogTitle></DialogHeader>
                <div className="space-y-4 text-sm">
                    {profile ? (
                        <div className="border rounded-md p-3 text-xs" style={{ borderColor: "var(--se-border)", background: "#f8fafc" }}>
                            <div><strong>{profile.name}</strong> ({profile.employee_code}) · {profile.tier}</div>
                            <div className="text-stone-500">{profile.department || "—"} · {profile.location || "—"}</div>
                        </div>
                    ) : null}
                    <div>
                        <div className="text-xs font-semibold text-stone-600 mb-1">Earnings</div>
                        <table className="w-full text-xs">
                            <tbody>
                                <Row label={`Base (${slip.prorate_ratio || 1} × ₹${inr(slip.base_salary)})`} value={slip.prorated_base} />
                                {slip.ot_amount > 0 ? <Row label={`Overtime (${slip.ot_hours}h × ₹${inr(slip.ot_rate)})`} value={slip.ot_amount} /> : null}
                                {slip.incentive > 0 ? <Row label="Incentive" value={slip.incentive} tone="emerald" /> : null}
                                {slip.commission > 0 ? <Row label="Sales commission" value={slip.commission} tone="emerald" /> : null}
                                {slip.penalty > 0 ? <Row label="Penalty" value={-Math.abs(slip.penalty)} tone="amber" /> : null}
                                <Row label="Gross earned" value={slip.gross_earned} bold />
                            </tbody>
                        </table>
                    </div>
                    <div>
                        <div className="text-xs font-semibold text-stone-600 mb-1">Deductions</div>
                        <table className="w-full text-xs">
                            <tbody>
                                {slip.tds_deduction > 0 ? <Row label="TDS" value={-slip.tds_deduction} tone="amber" /> : null}
                                {slip.pf_employee_deduction > 0 ? <Row label="PF (employee share)" value={-slip.pf_employee_deduction} tone="amber" /> : null}
                                {slip.pt_deduction > 0 ? <Row label="Professional Tax" value={-slip.pt_deduction} tone="amber" /> : null}
                                {slip.advance_recovery > 0 ? <Row label="Advance EMI recovery" value={-slip.advance_recovery} tone="amber" /> : null}
                                <Row label="Total deductions" value={-deductions} bold />
                            </tbody>
                        </table>
                    </div>
                    <div className="border-t pt-3" style={{ borderColor: "var(--se-border)" }}>
                        <table className="w-full text-sm">
                            <tbody>
                                <Row label="Net take-home" value={slip.net_payable} bold big />
                            </tbody>
                        </table>
                    </div>
                    {slip.lop_days > 0 ? (
                        <div className="text-xs text-amber-700 border rounded-md p-2" style={{ borderColor: "#fde68a", background: "#fefce8" }}>
                            {slip.lop_days} loss-of-pay day{slip.lop_days === 1 ? "" : "s"} this month reduced the working-days prorate.
                        </div>
                    ) : null}
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={async () => {
                            try {
                                const res = await api.get(`/payroll/runs/${slip.id}/payslip`,
                                    { responseType: "blob" });
                                const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `payslip-${slip.month}.pdf`;
                                document.body.appendChild(a); a.click();
                                setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
                            } catch (e) { toast.error(formatApiError(e)); }
                        }}
                        data-testid="my-slip-download"
                    >
                        Download PDF
                    </Button>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Row({ label, value, bold, big, tone }) {
    const negative = value < 0;
    const color = tone === "amber" ? "#b45309" : tone === "emerald" ? "#047857" : (negative ? "#b45309" : "var(--se-ink)");
    return (
        <tr style={bold ? { borderTop: "1px solid var(--se-border)" } : {}}>
            <td className={`py-1 ${bold ? "font-medium" : "text-stone-600"}`}>{label}</td>
            <td className={`py-1 text-right se-num ${bold ? "font-medium" : ""} ${big ? "text-lg" : ""}`} style={{ color }}>
                {negative ? "− " : ""}₹ {inr(Math.abs(value || 0))}
            </td>
        </tr>
    );
}
